import { React, useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import './NoticeCreateBox.css';
import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import NewCommentBox from './NewCommentBox';
import NewCommentWriteBox from './NewCommentWriteBox';
import UserProfile from '../../../Component/Normal/UserProfile';
import NoticePictureInput from './NoticePictureInput';
import CustomScrollbar from '../../Share/Normal/CustomScrollbar';

import LoadingAnimation from '../../../Animation/NewLoading.json';
import DefaultLogoPath from '../../../image/DefaultLogo.png';
import { ReactComponent as PinIconSvg } from '../../../image/Pin.svg';
import { ReactComponent as URLIconSvg } from '../../../image/Url.svg';

const NewNoticeViewBox = forwardRef((props, ref) => {
  const { data, title, noticeId, storeLogo, rendering, isLoading } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [totalLength, setTotalLength] = useState(0);
  const [name, setName] = useState(undefined);
  const [commentId, setCommentId] = useState(-1);

  const [urls, setUrls] = useState([]);
  const [content, setContent] = useState('');
  const [initialPictures, setInitialPictures] = useState([]);

  const CommentRef = useRef();
  const InfoRef = useRef();
  const VideoScrollRef = useRef();

  useImperativeHandle(ref, () => ({}))

  useImperativeHandle(InfoRef, () => ({
    setName: (val) => {
      setName(val);
    },
    setCommentId: (val) => {
      setCommentId(val);
    },
  }))

  // 공지사항 데이터에서 URL·사진 추출 //
  useEffect(() => {
    if (!data) return;

    let newContent = data.content || '';
    let extracted = [];

    while (true) {
      let url = collectURL(newContent);

      if (url === '') break;
      else {
        newContent = newContent.replace(url, '');
        extracted.push(url.replaceAll(',', ''));
      }
    }
    setUrls(extracted);
    setContent(newContent);

    if (data.picture !== undefined && data.picture !== '') {
      setInitialPictures(data.picture.split('<-@->'));
    }
  }, [data])

  // 댓글·답글 총 개수 계산 //
  useEffect(() => {
    if (!data || !data.commentList) return;

    let count = data.commentList.length;

    for (let i = 0; i < data.commentList.length; i++) {
      count += data.commentList[i].replyList.length;
    }

    setTotalLength(count);
  }, [data])

  // 비디오 영역 휠 → 가로 스크롤 //
  useEffect(() => {
    const el = VideoScrollRef.current;
    if (!el) return;

    function onWheel(e) {
      if (el.scrollWidth <= el.clientWidth) return;
      const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      if (delta === 0) return;
      e.preventDefault();
      el.scrollLeft += delta;
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [urls])

  // 공지사항내에 링크 추출 //
  function collectURL(text) {
    let urlRegex = /(https?:\/\/[^ ]*)/;
    let url = text.match(urlRegex) === null ? '' : text.match(urlRegex)[1];

    return url;
  }

  // 링크 클릭시 //
  function onClickURL(url) {
    window.open(url, "_blank", "noreferrer");
  }

  // 유튜브링크인지 확인 //
  function decideYoutubeLink(url) {
    if (!url.includes('https://youtu.be/') && !url.includes('https://www.youtube.com/watch?v=')) {
      return false;
    }
    else if (url.includes('https://youtu.be/')
      && (url.split('https://youtu.be/')[0] !== '' || url.split('https://youtu.be/')[1] === '')) {
      return false;
    }
    else if (url.includes('https://www.youtube.com/watch?v=')
      && (url.split('https://www.youtube.com/watch?v=')[0] !== '' || url.split('https://www.youtube.com/watch?v=')[1] === '')) {
      return false;
    }
    else return true;
  }

  // 유튜브링크인지 확인후 Id 만 추출 //
  function decideYoutubeLinkVideoId(url) {
    if (url.includes('https://youtu.be/')) return url.replace('https://youtu.be/', '');
    else if (url.includes('https://www.youtube.com/watch?v=')) return url.replace('https://www.youtube.com/watch?v=', '')
  }

  if (isLoading) {
    return (
      <TopLevelWrapper>
        <LoadingView>
          <LottieWrapper>
            <Lottie animationData={LoadingAnimation} loop={true} />
          </LottieWrapper>
        </LoadingView>
      </TopLevelWrapper>
    );
  }

  return (
    <TopLevelWrapper>
      <ViewWrapper isDark={isDark}>
        <ComponentWrapper>
          <TitleWrapper>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('noticePage.titleLabel')}</Text>
            <FixingWrapper $isFixed={!!data.isFixed}>
              <PinIcon $isFixed={!!data.isFixed} />
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={data.isFixed ? theme.pointRed : theme.text}>{t('noticePage.pinToTop')}</Text>
            </FixingWrapper>
          </TitleWrapper>
          <TitleDisplayWrapper>
            <ProfileBoxWrapper>
              <UserProfile profile={storeLogo} defaultProfile={DefaultLogoPath} />
            </ProfileBoxWrapper>
            <TitleBox>{title}</TitleBox>
          </TitleDisplayWrapper>
        </ComponentWrapper>
        <ContentWrapper>
          <ComponentWrapper>
            <Title>{t('noticePage.contentLabel')}</Title>
            <ContentBox>{content}</ContentBox>
          </ComponentWrapper>
          {
            urls.some(url => !decideYoutubeLink(url)) &&
            <URLWrapper>
              {
                urls.map((urlData, index) => (
                  !decideYoutubeLink(urlData) &&
                  <URLTextBox key={'URLTextBox-' + index} onClick={() => onClickURL(urlData)}>
                    <InnerURLTextBox>
                      <URLIcon />
                      <TextWrapper>
                        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='pointer'>{urlData}</Text>
                      </TextWrapper>
                    </InnerURLTextBox>
                  </URLTextBox>
                ))
              }
            </URLWrapper>
          }
          {
            urls.some(url => decideYoutubeLink(url)) &&
            <VideoOuterWrapper $hasScrollbar={urls.filter(url => decideYoutubeLink(url)).length >= 2}>
              <VideoWrapper ref={VideoScrollRef}>
                {
                  urls.map((urlData, index) => (
                    decideYoutubeLink(urlData) &&
                    <VideoViewWrapper key={'VideoViewWrapper-' + index}>
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/` + decideYoutubeLinkVideoId(urlData)}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Embedded youtube" />
                    </VideoViewWrapper>
                  ))
                }
              </VideoWrapper>
              {
                urls.filter(url => decideYoutubeLink(url)).length >= 2 &&
                <ScrollbarWrapper>
                  <CustomScrollbar
                    direction='horizontal'
                    scrollRef={VideoScrollRef}
                    IsColorScrollBar={true}
                    isTrackThin={true}
                    showTrackAlways={false} />
                </ScrollbarWrapper>
              }
            </VideoOuterWrapper>
          }
        </ContentWrapper>
        <DivisionLine />
        <ComponentWrapper>
          <CommentTitleWrapper>
            <CommentTitle>{t('noticePage.comment.title')}</CommentTitle>
            <CommentCount>{totalLength}</CommentCount>
          </CommentTitleWrapper>
          <CommentBoxWrapper>
            {
              data.commentList && data.commentList.map((commentData, index) => (
                <NewCommentBox key={index} data={commentData} isLast={data.commentList.length === index + 1} rendering={rendering} commentRef={CommentRef} infoRef={InfoRef} />
              ))
            }
          </CommentBoxWrapper>
          <NewCommentWriteBox noticeId={noticeId} rendering={rendering} commentRef={CommentRef} name={name} commentId={commentId} />
        </ComponentWrapper>
      </ViewWrapper>
      <NoticePictureInput initialPictures={initialPictures} readOnly={true} />
    </TopLevelWrapper>
  )
})

export default NewNoticeViewBox;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 1035px;
  min-height: 535px;

  gap: 20px;

  margin-top: 30px;
  margin-bottom: 96px;
  padding-bottom: 68px;
`;

const LoadingView = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: 160px;

  width: 100%;
  height: 100%;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const ViewWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 10px 0;

  box-sizing: border-box;
  width: 590px;

  gap: 20px;
`;

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  padding: 0px 20px;

  box-sizing: border-box;
  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  width: 100%;
  height: 26px;
`;

const FixingWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 3px;

  white-space: nowrap;

  transition: all 0.3s ease-in-out;
`;

const PinIcon = styled(PinIconSvg)`
  width: 16px;
  height: 16px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.$isFixed ? props.theme.pointRed : props.theme.text};
    transition: fill 0.3s ease-in-out;
  }
`;

const TitleDisplayWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 5px;
`;

const ProfileBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  max-width: 32px;
  max-height: 32px;

  border-radius: 5px;

  overflow: hidden;

  background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  padding: 0 10px;

  box-sizing: border-box;
  width: 100%;
  height: 32px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};
`;

const Title = styled.p`
  display: flex;
  flex-direction: row;

  height: 26px;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0 0 0 0;

  color: ${props => props.theme.text};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 550px;

  gap: 20px;
`;

const ContentBox = styled.pre`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 12px 14px;

  box-sizing: border-box;
  width: 100%;
  min-height: 200px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  white-space: pre-wrap;
  word-break: break-all;

  font-family: NotoSansKR-Medium;
  font-size: 14px;
  line-height: 22px;

  margin: 0;

  color: ${props => props.theme.text};
`;

const DivisionLine = styled.div`
  margin: 10px 30px;

  width: 530px;
  height: 1px;

  background-color: ${props => props.theme.line};
`;

const URLWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin: 0 20px;

  width: 550px;

  gap: 4px;
`;

const URLTextBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 15px;

  box-sizing: border-box;
  width: 100%;
  height: 52px;

  gap: 10px;

  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const InnerURLTextBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 1;
  min-width: 0;
  height: 22px;

  gap: 10px;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: 22px;

  overflow: hidden;
`;

const VideoOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin: 0 20px;
  margin-bottom: ${props => props.$hasScrollbar ? '0' : '25px'};

  width: 550px;
`;

const VideoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;

  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ScrollbarWrapper = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const VideoViewWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-end;

  width: 300px;
  height: 169px;

  flex-shrink: 0;

  overflow: hidden;

  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  & iframe {
    display: block;
    border: none;
    background-color: ${props => props.theme.subBackground};
  }
`;

const URLIcon = styled(URLIconSvg)`
  width: 20px;
  height: 20px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.text};
    transition: fill 0.3s ease-in-out;
  }
`;

const CommentTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 4px;

  width: 100%;
`;

const CommentTitle = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0;
`;

const CommentCount = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.subText};

  margin: 0;
`;

const CommentBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 14px;
  margin-bottom: 24px;

  width: 100%;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.cursor};
    }
  }
`;
